import { ReactElement } from 'react';
import { Box, HStack, Text, Image, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { RelevantYearName, ContentType } from '@novaheal/types';
import { useReadingTime } from 'hooks/api/backend/article/getReadingTime';
import { useIsRead } from 'hooks/utils/useIsRead';
import { useFirebase } from 'hooks/useFirebase';
import { NovaIcon } from 'components/ui/novaIcon';
import { VideoFileIcon } from 'assets/icons/v2';
import { ArticleEntryInfoBar } from './ArticleEntryInfoBar';
import { LikeButton } from '../../atoms/LikeButton';
import {
  useIsInModal,
  useModalNavigation,
} from '../Modal/variants/worksheet/components/ModalNavigationContext';

type Props = {
  id: string;
  title: string;
  imageUrl: string;
  subtitle: string | null;
  hasVideo: boolean;
  relevantYearNames: RelevantYearName[];
  showSubtitle?: boolean;

  // Favorite specific
  shouldShowWarning?: boolean;
  afterLikeClickFn?: () => void;
  entryIsLiked?: boolean;
};

const IMAGE_WIDTH = '80px';

export const ArticleEntry = ({
  id,
  title,
  imageUrl,
  subtitle,
  hasVideo,
  relevantYearNames,
  showSubtitle = true,

  // Favorite specific
  shouldShowWarning,
  afterLikeClickFn,
  entryIsLiked,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const isInModal = useIsInModal();
  const modalNavigation = useModalNavigation();

  const { data: readingTime = 0 } = useReadingTime(id);
  const { isRead } = useIsRead(id);
  const { logFirebaseClick } = useFirebase();
  const handleArticleClick = (): void => {
    logFirebaseClick('article-card', title);
    if (isInModal && modalNavigation) {
      modalNavigation.navigate({
        name: 'article',
        params: { id },
      });
      return;
    }
    navigate(`${ProtectedRoutes.ARTICLE}/${id}`);
  };

  return (
    <Box>
      <Box
        p="20px 10px 0px"
        onClick={handleArticleClick}
        cursor="pointer"
        width="100%"
        _hover={{ bg: 'background.canvas' }}
        _active={{ bg: 'background.canvas' }}
      >
        <HStack gap="25px" width="100%" position="relative" borderRadius="10px">
          <Box position="relative" borderRadius="7px" height="90px" width={IMAGE_WIDTH}>
            {hasVideo && (
              <Box
                position="absolute"
                top="0"
                right="0"
                zIndex={1}
                boxSize="28px"
                background="grey.white"
                display="grid"
                placeItems="center"
                transform="translate(28%, -8%)"
                borderRadius="100%"
              >
                <NovaIcon icon={VideoFileIcon} size="l" color="icon.info" />
              </Box>
            )}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              position="relative"
              width="100%"
              height="100%"
              borderRadius="7px"
              overflow="hidden"
            >
              <Image src={imageUrl} objectFit="cover" maxW="none" maxHeight="100%" />
            </Box>
          </Box>
          <VStack
            alignItems="flex-start"
            alignSelf="stretch"
            flexDirection="column"
            width="100%"
            gap="10px"
          >
            {showSubtitle && (
              <Text textStyle="body.s.regular" color="text.tertiary">
                {subtitle}
              </Text>
            )}
            <Text textStyle="h4.medium" color="text.primary" maxW="calc(100% - 45px)" lineClamp={2}>
              {title}
            </Text>
            <Box width="100%" mt="5px">
              <ArticleEntryInfoBar
                readingTime={readingTime}
                relevantYears={relevantYearNames}
                isRead={isRead}
              />
            </Box>
          </VStack>
          <Box pos="absolute" bottom="0" right="0">
            <LikeButton
              id={id}
              contentType={ContentType.ARTICLE}
              name={title}
              shouldShowWarning={shouldShowWarning}
              afterLikeClickFn={afterLikeClickFn}
              entryIsLiked={entryIsLiked}
            />
          </Box>
        </HStack>
        <Box
          h="1px"
          w={`calc(100% - ${IMAGE_WIDTH} - 6px)`}
          backgroundColor="border.accent"
          ml="auto"
          mt="15px"
        />
      </Box>
    </Box>
  );
};
